import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

interface Definition {
  customFieldDefinitionId: string;
  fieldKey: string;
  labelAr: string;
  labelEn: string | null;
  fieldType: number; // 1 text 2 number 3 date 4 boolean 5 select
  options: string | null;
  isRequired: boolean;
}
interface EntityFields {
  definitions: Definition[];
  values: Record<string, string | null>;
}

// Self-contained panel that loads a tenant's custom-field definitions for
// an entity type + the current values for one entity, renders typed
// inputs, and saves them. Renders nothing when the tenant has defined no
// fields, so it's safe to drop onto any edit form.
export function CustomFieldsPanel({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Record<string, string | null>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['custom-fields', entityType, entityId] as const,
    queryFn: async () =>
      (await apiClient.get<EntityFields>('/api/custom-fields/values', {
        params: { entityType, entityId },
      })).data,
  });

  useEffect(() => {
    if (data) setDraft(data.values ?? {});
  }, [data]);

  const save = useMutation({
    mutationFn: async () =>
      apiClient.put('/api/custom-fields/values', { entityType, entityId, values: draft }),
    onSuccess: () => notifications.show({ color: 'green', title: t('customFields.saved'), message: '' }),
    onError: (err) => notifications.show({
      color: 'red', title: t('common.toasts.errorTitle'),
      message: (err as { response?: { data?: { title?: string } } }).response?.data?.title ?? '',
    }),
  });

  if (isLoading) return <Loader size="sm" color="wheelsRed" />;
  if (!data || data.definitions.length === 0) return null;

  const set = (id: string, v: string | null) => setDraft((p) => ({ ...p, [id]: v }));

  return (
    <Paper p="md" withBorder>
      <Text fw={600} size="sm" mb="sm">{t('customFields.title')}</Text>
      <Stack gap="sm">
        {data.definitions.map((def) => {
          const id = def.customFieldDefinitionId;
          const val = draft[id] ?? '';
          const label = def.labelAr;
          if (def.fieldType === 2) {
            return (
              <NumberInput key={id} label={label} withAsterisk={def.isRequired}
                value={val === '' ? '' : Number(val)} decimalScale={3}
                onChange={(v) => set(id, v === '' ? null : String(v))} />
            );
          }
          if (def.fieldType === 3) {
            return (
              <DatePickerInput key={id} label={label} withAsterisk={def.isRequired} clearable
                value={val ? new Date(val) : null}
                onChange={(d) => set(id, d ? toIso(d) : null)} />
            );
          }
          if (def.fieldType === 4) {
            return (
              <Switch key={id} label={label} checked={val === 'true'}
                onChange={(e) => set(id, e.currentTarget.checked ? 'true' : 'false')} />
            );
          }
          if (def.fieldType === 5) {
            return (
              <Select key={id} label={label} withAsterisk={def.isRequired} clearable
                value={val || null} onChange={(v) => set(id, v)}
                data={(def.options ?? '').split(';').map((o) => o.trim()).filter(Boolean)} />
            );
          }
          return (
            <TextInput key={id} label={label} withAsterisk={def.isRequired}
              value={val} onChange={(e) => set(id, e.currentTarget.value)} />
          );
        })}
        <Group justify="flex-end">
          <Button size="xs" color="wheels" loading={save.isPending} onClick={() => save.mutate()}>
            {t('customFields.save')}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

function toIso(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
